import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {TrendingUp, Eye, EyeOff, AlertCircle, Mail, Lock, Sparkles, CheckCircle2, ArrowLeft, ShieldCheck} from 'lucide-react';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Label} from '@/components/ui/label';
import {Card} from '@/components/ui/card';
import {Badge} from '@/components/ui/badge';
import {useAuth} from '@/contexts/AuthContext';
import {useBranding} from '@/contexts/BrandingContext';
import {toast} from 'sonner';
import {ROLES, DEFAULT_PATH_FOR_ROLE} from '@/lib/roles';

const PLATFORM_LOGO = '/branding/condutor-online-logo.png';
const PLATFORM_NAME = 'condutor.online';

export const AccountPage = () => {
    const {user, changeMyPassword} = useAuth();
    const {branding} = useBranding();
    const navigate = useNavigate();

    const [currentPwd, setCurrentPwd] = useState('');
    const [newPwd, setNewPwd] = useState('');
    const [confirmPwd, setConfirmPwd] = useState('');
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    const isSuper = user?.role === 'superadmin';
    const homePath = DEFAULT_PATH_FOR_ROLE[user?.role] || '/';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess(false);
        if (!currentPwd || !newPwd || !confirmPwd) {
            setError('Preencha todos os campos.');
            return;
        }
        if (newPwd !== confirmPwd) {
            setError('A nova palavra-passe e a confirmação não coincidem.');
            return;
        }
        if (newPwd.length < 8) {
            setError('A nova palavra-passe tem de ter pelo menos 8 caracteres.');
            return;
        }
        if (newPwd === currentPwd) {
            setError('A nova palavra-passe é igual à atual.');
            return;
        }
        setLoading(true);
        const r = await changeMyPassword({currentPassword: currentPwd, newPassword: newPwd});
        setLoading(false);
        if (!r.ok) {
            setError(r.error);
            return;
        }
        setCurrentPwd('');
        setNewPwd('');
        setConfirmPwd('');
        setSuccess(true);
        toast.success('Palavra-passe atualizada com sucesso.');
    };

    return (
        <div className="min-h-screen w-full bg-slate-950 flex items-stretch" data-testid="account-page">
            {/* Left brand panel — igual ao login, mas em tom informativo */}
            <div className="hidden lg:flex flex-1 relative overflow-hidden">
                <div className="absolute inset-0" style={{
                    background: isSuper
                        ? 'radial-gradient(circle at 25% 35%, rgba(124,58,237,0.45), transparent 55%), radial-gradient(circle at 80% 75%, rgba(234,179,8,0.18), transparent 55%), linear-gradient(135deg, #1e1b4b 0%, #0a0f1f 100%)'
                        : 'radial-gradient(circle at 25% 35%, rgba(37,99,235,0.45), transparent 55%), radial-gradient(circle at 80% 75%, rgba(234,179,8,0.18), transparent 55%), linear-gradient(135deg, #0c1a3a 0%, #0a0f1f 100%)'
                }}/>
                <div className="absolute inset-0 opacity-30" style={{
                    backgroundImage: 'linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)',
                    backgroundSize: '60px 60px',
                }}/>
                <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{backgroundColor: '#facc15'}}/>
                <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
                    <div className="flex items-center gap-5 flex-wrap">
                        <div className="flex items-center gap-4">
                            {isSuper ? (
                                <div className="rounded-xl p-3" style={{backgroundColor: '#7c3aed'}}>
                                    <ShieldCheck size={32} className="text-white"/>
                                </div>
                            ) : branding.logo && branding.logo !== PLATFORM_LOGO ? (
                                <img src={branding.logo} alt={branding.companyName || 'Cliente'} className="h-16 w-auto rounded-xl bg-white/10 backdrop-blur p-1.5 ring-1 ring-white/10"/>
                            ) : (
                                <div className="rounded-xl p-3" style={{backgroundColor: branding.accentColor || '#4f46e5'}}>
                                    <TrendingUp size={32} className="text-white"/>
                                </div>
                            )}
                            <div>
                                <h1 className="font-secondary font-bold text-2xl xl:text-3xl leading-tight">
                                    {isSuper ? 'Gestor do Sistema' : (branding.companyName || PLATFORM_NAME)}
                                </h1>
                                {!isSuper && branding.companySubtitle && <p className="text-sm text-slate-300">{branding.companySubtitle}</p>}
                                {isSuper && <p className="text-sm text-slate-300">Portal de administração do sistema</p>}
                            </div>
                        </div>
                        <div className="flex items-center gap-2 ml-auto">
                            <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Powered by</span>
                            <div className="bg-black rounded-lg px-2.5 py-1.5 ring-1 ring-white/10">
                                <img src={PLATFORM_LOGO} alt={PLATFORM_NAME} className="h-7 w-auto block"/>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-6 max-w-lg">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                             style={{backgroundColor: '#facc15', color: '#0c1a3a'}}>
                            <Sparkles size={12}/>
                            Segurança da conta
                        </div>
                        <h2 className="font-secondary font-bold text-4xl xl:text-5xl leading-tight">
                            Mantenha a sua conta <br/>
                            <span style={{color: '#facc15'}}>segura</span>.
                        </h2>
                        <p className="text-slate-300 text-base leading-relaxed">
                            A sua palavra-passe protege todos os dados da frota, tags, ocorrências e canais de notificação. Use uma combinação única e altere-a regularmente.
                        </p>
                        <ul className="space-y-2 text-sm text-slate-300">
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400"/> Mínimo 4 caracteres (recomenda-se ≥ 8).</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400"/> Combine letras, números e símbolos.</li>
                            <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-400"/> Nunca partilhe a sua palavra-passe.</li>
                        </ul>
                    </div>

                    <div className="flex items-center justify-between text-xs text-slate-500">
                        <p>© {new Date().getFullYear()} {branding.companyName || PLATFORM_NAME} — Todos os direitos reservados.</p>
                        <div className="flex items-center gap-1.5">
                            <span>Powered by</span>
                            <span className="font-bold text-slate-300">{PLATFORM_NAME}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right form */}
            <div className="flex-1 flex items-center justify-center p-6 lg:p-12 bg-gradient-to-br from-slate-50 to-blue-50">
                <div className="w-full max-w-md">
                    <Button variant="ghost" size="sm" onClick={() => navigate(homePath)} className="mb-4" data-testid="account-back-btn">
                        <ArrowLeft size={14} className="mr-1"/> Voltar
                    </Button>

                    <Card className="p-8 shadow-2xl border-blue-100 relative overflow-hidden">
                        <div className="absolute top-0 left-0 right-0 h-1.5" style={{backgroundColor: '#facc15'}}/>
                        <div className="mb-6">
                            <h2 className="text-2xl font-secondary font-bold text-slate-900">A sua conta</h2>
                            <p className="text-sm text-slate-500 mt-1">Edite os dados de acesso. O email não pode ser alterado.</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4" data-testid="account-form">
                            {/* Email (read-only) */}
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="account-email">Email</Label>
                                <div className="relative">
                                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input
                                        id="account-email"
                                        type="email"
                                        value={user?.email || ''}
                                        readOnly
                                        disabled
                                        className="pl-9 bg-slate-100 text-slate-500 cursor-not-allowed"
                                        data-testid="account-email"
                                    />
                                    <Badge variant="outline" className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] uppercase tracking-wider bg-slate-50">
                                        não editável
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-2 text-[11px] text-slate-500">
                                    <span>Perfil:</span>
                                    <Badge className="bg-slate-100 text-slate-700 border-slate-200">{ROLES[user?.role]?.label || user?.role || '—'}</Badge>
                                    {user?.name && <span>· {user.name}</span>}
                                </div>
                            </div>

                            <div className="h-px bg-slate-100 my-2"/>

                            {/* Current password */}
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="current-pwd">Palavra-passe atual</Label>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input id="current-pwd" type={showCurrent ? 'text' : 'password'} autoComplete="current-password"
                                           value={currentPwd} onChange={e => setCurrentPwd(e.target.value)}
                                           className="pl-9 pr-10" data-testid="account-current-pwd"/>
                                    <button type="button" onClick={() => setShowCurrent(s => !s)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                                        {showCurrent ? <EyeOff size={15}/> : <Eye size={15}/>}
                                    </button>
                                </div>
                            </div>

                            {/* New password */}
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="new-pwd">Nova palavra-passe</Label>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input id="new-pwd" type={showNew ? 'text' : 'password'} autoComplete="new-password"
                                           value={newPwd} onChange={e => setNewPwd(e.target.value)}
                                           className="pl-9 pr-10" data-testid="account-new-pwd"/>
                                    <button type="button" onClick={() => setShowNew(s => !s)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                                        {showNew ? <EyeOff size={15}/> : <Eye size={15}/>}
                                    </button>
                                </div>
                            </div>

                            {/* Confirm */}
                            <div className="space-y-2">
                                <Label className="text-sm" htmlFor="confirm-pwd">Confirmar nova palavra-passe</Label>
                                <div className="relative">
                                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"/>
                                    <Input id="confirm-pwd" type={showNew ? 'text' : 'password'} autoComplete="new-password"
                                           value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)}
                                           className="pl-9 pr-10" data-testid="account-confirm-pwd"/>
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="account-error">
                                    <AlertCircle size={14} className="mt-0.5 flex-shrink-0"/>
                                    <span>{error}</span>
                                </div>
                            )}
                            {success && (
                                <div className="flex items-start gap-2 p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-700" data-testid="account-success">
                                    <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0"/>
                                    <span>Palavra-passe atualizada com sucesso. Use-a no próximo início de sessão.</span>
                                </div>
                            )}

                            <Button type="submit" className="w-full bg-blue-700 hover:bg-blue-800 h-11 text-white" disabled={loading} data-testid="account-save-btn">
                                {loading ? 'A guardar...' : 'Atualizar palavra-passe'}
                            </Button>
                        </form>
                    </Card>

                    <p className="text-center text-xs text-slate-500 mt-6">
                        Problemas a aceder? Contacte o administrador da sua organização ou o suporte {PLATFORM_NAME}.
                    </p>
                </div>
            </div>
        </div>
    );
};
